import { ApolloClient, useQuery } from '@apollo/client';
import { IItinerary } from 'common/types';
import { getTripInfo } from 'common/utils';
import { TripType } from 'contexts/searchParams';
import GET_AIRPORT_DETAIL_LIST from './airportDetailList.gql';
import GET_AIRPORTS_IN_CITY from './airportsInCity.old.gql';

export interface ICityImage {
  imagePath: string;
  copyright: string;
  copyrightUrl: string;
}

export interface IAirportDetail {
  iataCode: string;
  cityName: string;
  // cityNameEn: string;
  isDomestic: boolean;
  event: boolean;
  cityImage: ICityImage;
  airportName: string;
  // airportNameEn: string;
  countryName: string;
  // countryNameEn: string;
}

export interface IAirportInfos {
  airportDetailList: IAirportDetail[];
}

const getUniqueLocationCodes = (itineraries: IItinerary[]) =>
  Array.from(
    new Set(
      itineraries.flatMap(({ departureAirport, arrivalAirport }) => [
        departureAirport,
        arrivalAirport,
      ]),
    ),
  );

const getSummarizedLocationCodes = (itineraries: IItinerary[]) => {
  const tripType = getTripInfo(itineraries);
  return itineraries
    .flatMap(({ departureAirport, arrivalAirport }) => [
      departureAirport,
      arrivalAirport,
    ])
    .reduce<string[]>((prev, cur) => {
      const last = prev[prev.length - 1];
      return last !== cur ? prev.concat(cur) : prev;
    }, [])
    .slice(0, tripType === TripType.RT ? -1 : undefined);
};

async function getAirportList(
  apolloClient: ApolloClient<object>,
  airportCodes: string[],
) {
  try {
    const { data } = await apolloClient.query<IAirportInfos>({
      query: GET_AIRPORT_DETAIL_LIST,
      variables: { airportCodes: airportCodes.filter(Boolean) },
    });
    return data.airportDetailList;
  } catch {
    return [];
  }
}

async function getAirportsInCity({
  apolloClient,
  cityCode,
  isSkip = false,
}: {
  apolloClient: ApolloClient<object>;
  cityCode: string;
  isSkip?: boolean;
}) {
  try {
    if (isSkip) return [];
    const { data } = await apolloClient.query<{
      airportsInCity: IAirportDetail[];
    }>({
      query: GET_AIRPORTS_IN_CITY,
      variables: { cityCode },
    });
    return data.airportsInCity;
  } catch {
    return [];
  }
}

const useAirportDetailList = (airportCodes: string[], skip: boolean = false) =>
  useQuery<IAirportInfos>(GET_AIRPORT_DETAIL_LIST, {
    variables: { airportCodes: airportCodes.filter(Boolean) },
    skip,
  });

export default useAirportDetailList;

export {
  getAirportList,
  getAirportsInCity,
  getSummarizedLocationCodes,
  getUniqueLocationCodes,
};
